import {gql} from '@apollo/client';

import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import {
  ServerResolutionResult,
  StringResolutionType,
  ValidateParentStorableQuery,
} from '../string-resolution/StringResolutionConstants';
import {
  GetStorablePermissionsAndSchema,
  GetStorablePermissionsAndSchemaVariables,
} from './__generated/GetStorablePermissionsAndSchema';

const GET_STORABLE_PERMISSIONS_AND_SCHEMA_QUERY = gql`
  query GetStorablePermissionsAndSchema($storableId: StrictString!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storable(id: $storableId) {
      ... on StorableInterface {
        id
        name
        permissions {
          appendable
        }
        storableSchema {
          ... on GridSchemaInterface {
            containerSchema {
              id
            }
            width
            height
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doQuery(storableId: string): Promise<GetStorablePermissionsAndSchema> {
    return await doQueryWithoutCache<
      GetStorablePermissionsAndSchema,
      GetStorablePermissionsAndSchemaVariables
    >({query: GET_STORABLE_PERMISSIONS_AND_SCHEMA_QUERY}, {variables: {storableId}});
  },
};

export default async function validateParentStorable(
  query: ValidateParentStorableQuery
): Promise<ServerResolutionResult> {
  const data = await stubbableFunctions.doQuery(query.storableId);
  const storable = data.storable;
  if (isInaccessible(storable)) {
    // TODO(BNCH-15696): inaccessible chips should be marked as invalid
    return {
      type: StringResolutionType.CUSTOM,
      error: undefined,
      resolvedValue: {
        typedValue: null,
      },
    };
  }

  if (query.isForTransfer && !storable.permissions.appendable) {
    return {
      type: undefined,
      error: `${storable.name} is read-only.`,
      resolvedValue: undefined,
    };
  }

  if (
    query.expectedGridContainerSchemaId &&
    'containerSchema' in storable.storableSchema &&
    storable.storableSchema.containerSchema &&
    storable.storableSchema.containerSchema.id !== query.expectedGridContainerSchemaId
  ) {
    return {
      type: undefined,
      error: `Location is not compatible with this table's container schema.`,
      resolvedValue: undefined,
    };
  }

  // Everything is OK
  return {
    type: StringResolutionType.CUSTOM,
    error: undefined,
    resolvedValue: {
      typedValue: null,
    },
  };
}
